import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import $ from "jquery";
import { useParams, useLocation } from "react-router-dom";
import "../styles/previewDigitalPPTBrowser.css";
import LoadingIcon from "../images/Loading_icon.gif";
import Swal from "sweetalert2";
import ExistingDigital from "../components/PreviewDigitalPPT/ExistingDigital";
// import AutomatedSummary from '../components/PreviewDigitalPPT/AutomatedSummary';
import successIcon from "../images/successGreenIcon.png";
import errorIcon from "../images/errorRedIcon.png";
import { UrlContext } from "../contexts/UrlContext";
import SharedInfoSelection from "../components/PreviewDigitalPPT/SharedInfoSelection";
import DigitalProfileSlide from "../components/PreviewDigitalPPT/DigitalProfileSlide";
import {
  BriefcaseBusiness,
  CalendarClock,
  Coins,
  Globe2,
  GraduationCap,
  Hash,
  History,
  ImagePlus,
  Languages,
  MapPin,
  FileText,
  Target,
  UserRound,
  Trash2,
  Upload,
  X,
  Sparkles,
} from "lucide-react";

const modules = {
  toolbar: {
    container: "#pdp2-summary-toolbar",
  },
};

const formats = ["size", "bold", "italic", "underline", "list"];

const PDP2_SECTION_DB_FIELDS = {
  profilePicture: "hasPictureSection",
  identity: "hasNameSection",
  position: "hasPositionSection",
  location: "hasLocationSection",
  region: "hasRegionSection",
  language: "hasLanguageSection",
  totalExperience: "hasTotalExperienceSection",
  availability: "hasAvailabilitySection",
  rate: "hasRateSection",
  summary: "hasSummarySection",
  matching: "hasMatchingSection",
  experience: "hasExperienceSection",
  education: "hasEducationSection",
};

const pdp2StoredBoolean = (value, fallback = true) => {
  if (value === null || value === undefined) {
    return fallback;
  }

  return Boolean(value);
};


function resolvePdp2ProfileImage(value) {
  const imageValue = String(value || "").trim();
  if (!imageValue) return "";

  if (/^(data:|blob:|https?:\/\/)/i.test(imageValue)) {
    return imageValue;
  }

  return `https://images.expertpowerhouse.com${
    imageValue.startsWith("/") ? "" : "/"
  }${imageValue}`;
}

function Pdp2ExpertImage({
  proj_id,
  exp_id,
  exProfilePicture,
  setExProfilePicture,
  readOnly = false,
}) {
  const { API_URL } = useContext(UrlContext);
  const fileInputRef = useRef(null);

  const [showUpload, setShowUpload] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [pendingProfilePictureValue, setPendingProfilePictureValue] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [uploadError, setUploadError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [exProfilePictureLoading, setExProfilePictureLoading] = useState(false);
  const [exProfilePictureSuccess, setExProfilePictureSuccess] = useState(false);
  const [exProfilePictureError, setExProfilePictureError] = useState(false);

  const displayedImage = resolvePdp2ProfileImage(exProfilePicture);

  const saveExProfilePicture = (imageURL) => {
    if (readOnly) return;
    setExProfilePictureError(false);
    setExProfilePictureLoading(true);

    const url = `${API_URL}ppt-save-profile-picture/${proj_id}/${exp_id}`;

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ profilePicture: imageURL }),
    })
      .then((res) => res.json())
      .then((data) => {
        setExProfilePictureLoading(false);

        if (data.status === "success") {
          setExProfilePictureSuccess(true);

          setTimeout(() => {
            setExProfilePictureSuccess(false);
            setExProfilePicture(data.file);
          }, 1500);
        } else {
          setExProfilePictureError(true);
        }
      })
      .catch(() => {
        setExProfilePictureLoading(false);
        setExProfilePictureError(true);
      });
  };

  const openUpload = () => {
    if (readOnly) return;
    setPreviewImage("");
    setPendingProfilePictureValue("");
    setSelectedFileName("");
    setUploadError("");
    setIsDragging(false);
    setExProfilePictureError(false);
    setShowUpload(true);
  };

  const closeUpload = () => {
    setPreviewImage("");
    setPendingProfilePictureValue("");
    setSelectedFileName("");
    setUploadError("");
    setIsDragging(false);
    setShowUpload(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const previewFile = (file) => {
    if (!file) return;

    const fileName = String(file.name || "Dropped image");
    const fileType = String(file.type || "").toLowerCase();

    // Some browsers/OS drag sources provide an empty MIME type even for a
    // perfectly valid image. In that case, accept common image extensions.
    const looksLikeImage =
      fileType.startsWith("image/") ||
      /\.(png|jpe?g|webp|gif|bmp|avif|heic|heif)$/i.test(fileName) ||
      !fileType;

    if (!looksLikeImage) {
      setPreviewImage("");
      setSelectedFileName("");
      setUploadError("Please choose or drop an image file.");
      return;
    }

    setUploadError("");
    setPendingProfilePictureValue("");

    const reader = new FileReader();

    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";

      if (!result) {
        setPreviewImage("");
        setSelectedFileName("");
        setUploadError("The selected image could not be read.");
        return;
      }

      setPreviewImage(result);
      setSelectedFileName(fileName);
    };

    reader.onerror = () => {
      setPreviewImage("");
      setSelectedFileName("");
      setUploadError("The selected image could not be read.");
    };

    reader.readAsDataURL(file);
  };

  const dataURItoBlob = (dataURI) => {
    const byteString = atob(dataURI.split(",")[1]);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i += 1) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    return new Blob([arrayBuffer], { type: "image/png" });
  };

  const setCopiedProfilePicturePreview = (payload) => {
    if (!payload) return false;

    const imageValue = String(payload.imageValue || "").trim();
    const imageUrl = String(payload.imageUrl || "").trim();
    const previewUrl = imageUrl || resolvePdp2ProfileImage(imageValue);

    if (!previewUrl) return false;

    setPreviewImage(previewUrl);
    setPendingProfilePictureValue(imageValue || imageUrl);
    setSelectedFileName(
      payload.expertID
        ? `Digital profile image · Expert ${payload.expertID}`
        : "Digital profile image"
    );
    setUploadError("");

    return true;
  };

  const parsePdp2DragPayload = (rawValue) => {
    if (!rawValue) return null;

    try {
      return JSON.parse(rawValue);
    } catch (error) {
      try {
        return JSON.parse(decodeURIComponent(rawValue));
      } catch (decodeError) {
        return null;
      }
    }
  };

  const handleProfileImageDragStart = (event) => {
    if (!displayedImage || !event.dataTransfer) return;

    const payload = {
      source: "PreviewDigitalPPT",
      imageValue: exProfilePicture || "",
      imageUrl: displayedImage,
      expertID: exp_id,
      projectID: proj_id,
    };

    const payloadJson = JSON.stringify(payload);
    const encodedPayload = encodeURIComponent(payloadJson);

    event.dataTransfer.effectAllowed = "copy";

    try {
      event.dataTransfer.setData(
        "application/x-eph-profile-picture",
        payloadJson
      );
    } catch (error) {
      // Browser may reject custom MIME types; normal fallbacks remain.
    }

    try {
      event.dataTransfer.setData(
        "text/html",
        `<img src="${displayedImage}" data-eph-profile-picture="${encodedPayload}" alt="EPH profile picture" />`
      );
    } catch (error) {
      // Ignore and keep the plain-text fallback.
    }

    try {
      event.dataTransfer.setData(
        "text/plain",
        `EPH_PROFILE_IMAGE:${encodedPayload}`
      );
    } catch (error) {
      // Ignore.
    }

    try {
      event.dataTransfer.setData("text/uri-list", displayedImage);
    } catch (error) {
      // Ignore.
    }
  };

  const getPdp2StoredImageValueFromUrl = (imageURL) => {
    if (!imageURL) return "";

    try {
      const parsedUrl = new URL(imageURL, window.location.origin);

      if (
        parsedUrl.hostname.toLowerCase() !==
        "images.expertpowerhouse.com"
      ) {
        return "";
      }

      return parsedUrl.pathname || "";
    } catch (error) {
      return "";
    }
  };

  const applyPdp2ImageUrlDirectly = (imageURL, label = "Digital profile image") => {
    const cleanURL = String(imageURL || "").trim();
    if (!cleanURL) return false;

    if (cleanURL.startsWith("data:image")) {
      setPreviewImage(cleanURL);
      setPendingProfilePictureValue("");
      setSelectedFileName(label);
      setUploadError("");
      return true;
    }

    const storedImageValue = getPdp2StoredImageValueFromUrl(cleanURL);

    if (!storedImageValue) {
      return false;
    }

    // For EPH-hosted profile images we do NOT fetch the image again.
    // We already know the persisted DB value from the URL pathname.
    setPreviewImage(cleanURL);
    setPendingProfilePictureValue(storedImageValue);
    setSelectedFileName(label);
    setUploadError("");

    return true;
  };

  const fetchExternalImageForPreview = async (
    imageURL,
    label = "Browser image"
  ) => {
    const cleanURL = String(imageURL || "").trim();

    if (!cleanURL) {
      return false;
    }

    if (applyPdp2ImageUrlDirectly(cleanURL, label)) {
      return true;
    }

    try {
      const response = await fetch(cleanURL);

      if (!response.ok) {
        return false;
      }

      const blob = await response.blob();

      previewFile(
        new File([blob], "browser-image", {
          type: blob.type || "image/png",
        })
      );

      return true;
    } catch (error) {
      console.error("Error fetching browser image:", error);
      return false;
    }
  };

  const getImageUrlFromHtml = (htmlValue) => {
    if (!htmlValue) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlValue, "text/html");

    return doc.querySelector("img")?.getAttribute("src") || "";
  };

  const handlePastedImage = async (event) => {
    const clipboardData = event.clipboardData;
    if (!clipboardData) return;

    setUploadError("");

    // 1. Native image clipboard data (Copy Image / screenshot / local copy).
    const pastedImageFile = Array.from(clipboardData.items || [])
      .filter(
        (item) =>
          item.kind === "file" &&
          String(item.type || "").toLowerCase().startsWith("image/")
      )
      .map((item) => item.getAsFile())
      .find(Boolean);

    if (pastedImageFile) {
      event.preventDefault();
      event.stopPropagation();
      previewFile(pastedImageFile);
      return;
    }

    // 2. HTML copied from a web page normally contains the <img src="...">.
    const pastedHtml = clipboardData.getData("text/html") || "";
    const pastedHtmlImageURL = getImageUrlFromHtml(pastedHtml);

    if (pastedHtmlImageURL) {
      event.preventDefault();
      event.stopPropagation();

      const loaded = await fetchExternalImageForPreview(
        pastedHtmlImageURL,
        "Pasted image"
      );

      if (!loaded) {
        setUploadError(
          "The pasted image could not be loaded. Try Copy Image directly, or save it locally and drop the file here."
        );
      }

      return;
    }

    // 3. Some browsers put only the image URL into text/plain.
    const pastedText = (clipboardData.getData("text/plain") || "").trim();

    if (/^(data:image|https?:\/\/)/i.test(pastedText)) {
      event.preventDefault();
      event.stopPropagation();

      const loaded = await fetchExternalImageForPreview(
        pastedText,
        "Pasted image"
      );

      if (!loaded) {
        setUploadError(
          "The pasted image URL could not be loaded. Try Copy Image directly, or save it locally and drop the file here."
        );
      }

      return;
    }

    setUploadError(
      "No image was found in the clipboard. Copy the image itself, then paste again."
    );
  };

  const handleDroppedImage = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    setUploadError("");

    const dataTransfer = event.dataTransfer;

    // Best path for dragging between two PreviewDigitalPPT pages. Use the
    // stored profile-picture value directly instead of fetching the CDN image.
    const directPdp2Payload = parsePdp2DragPayload(
      dataTransfer?.getData("application/x-eph-profile-picture")
    );

    if (setCopiedProfilePicturePreview(directPdp2Payload)) {
      return;
    }

    // Browser fallback: custom data embedded in normal dragged HTML.
    const pdp2DroppedHtml = dataTransfer?.getData("text/html") || "";

    if (pdp2DroppedHtml) {
      const pdp2Parser = new DOMParser();
      const pdp2Doc = pdp2Parser.parseFromString(
        pdp2DroppedHtml,
        "text/html"
      );
      const pdp2Img = pdp2Doc.querySelector(
        "img[data-eph-profile-picture]"
      );
      const pdp2EncodedPayload = pdp2Img?.getAttribute(
        "data-eph-profile-picture"
      );

      if (
        setCopiedProfilePicturePreview(
          parsePdp2DragPayload(pdp2EncodedPayload)
        )
      ) {
        return;
      }
    }

    // Last same-app fallback for browsers that keep only text/plain.
    const droppedPlainText = dataTransfer?.getData("text/plain") || "";

    if (droppedPlainText.startsWith("EPH_PROFILE_IMAGE:")) {
      const encodedPayload = droppedPlainText.slice(
        "EPH_PROFILE_IMAGE:".length
      );

      if (
        setCopiedProfilePicturePreview(
          parsePdp2DragPayload(encodedPayload)
        )
      ) {
        return;
      }
    }

    // Prefer DataTransferItem#getAsFile because some browsers expose the
    // dropped file there even when dataTransfer.files is unexpectedly empty.
    const droppedItemFile = Array.from(dataTransfer?.items || [])
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .find(Boolean);

    const droppedFile =
      droppedItemFile || Array.from(dataTransfer?.files || [])[0];

    if (droppedFile) {
      previewFile(droppedFile);
      return;
    }

    // Also support dragging an image from another browser tab/page.
    const droppedHtml = pdp2DroppedHtml;
    const droppedUri =
      dataTransfer?.getData("text/uri-list") ||
      droppedPlainText ||
      "";

    let imageURL = "";

    if (droppedHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(droppedHtml, "text/html");
      imageURL = doc.querySelector("img")?.getAttribute("src") || "";
    }

    if (!imageURL && /^\s*(data:image|blob:|https?:\/\/)/i.test(droppedUri)) {
      imageURL = droppedUri.trim().split(/\r?\n/)[0];
    }

    if (!imageURL) {
      setUploadError("No image was detected in the dropped item.");
      return;
    }

    if (
      applyPdp2ImageUrlDirectly(
        imageURL,
        "Dropped digital profile image"
      )
    ) {
      return;
    }

    const loaded = await fetchExternalImageForPreview(
      imageURL,
      "Dropped browser image"
    );

    if (!loaded) {
      setUploadError(
        "The dropped image could not be loaded. Please copy/paste the image or select it from your device instead."
      );
    }
  };

  const handleProfileUpload = () => {
    if (readOnly) return;
    if (!previewImage) return;

    const valueToSave =
      pendingProfilePictureValue || previewImage;

    setExProfilePicture(valueToSave);
    setShowUpload(false);
    saveExProfilePicture(valueToSave);
    setPreviewImage("");
    setPendingProfilePictureValue("");
    setSelectedFileName("");
    setUploadError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleProfileRemove = () => {
    if (readOnly) return;
    setExProfilePicture("");
    setPreviewImage("");
    setPendingProfilePictureValue("");
    setSelectedFileName("");
    setUploadError("");
    saveExProfilePicture("");
  };

  const uploadModal =
    !readOnly && showUpload && typeof document !== "undefined"
      ? createPortal(
          <div
            className="pdp2-image-modal-backdrop"
            role="presentation"
            data-pdp2-print-hide="true"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                closeUpload();
              }
            }}
          >
            <div
              className="pdp2-image-modal"
              role="dialog"
              onPasteCapture={handlePastedImage}
              aria-modal="true"
              aria-labelledby="pdp2-image-modal-title"
            >
              <div className="pdp2-image-modal-header">
                <div>
                  <span className="pdp2-image-modal-eyebrow">Digital profile</span>
                  <h3 id="pdp2-image-modal-title">Profile picture</h3>
                  <p>
                    Choose a clear image. It will be cropped to the square profile
                    frame used on the final client profile.
                  </p>
                </div>

                <button
                  type="button"
                  className="pdp2-image-modal-close"
                  onClick={closeUpload}
                  aria-label="Close image upload"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>

              <div className="pdp2-image-modal-body">
                <div
                  className={`pdp2-image-dropzone ${
                    isDragging ? "pdp2-image-dropzone-active" : ""
                  }`}
                  onDragEnterCapture={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragOverCapture={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsDragging(true);
                  }}
                  onDragLeave={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    // Ignore dragleave events caused only by moving between
                    // children inside the same dropzone.
                    if (
                      event.currentTarget.contains(event.relatedTarget)
                    ) {
                      return;
                    }

                    setIsDragging(false);
                  }}
                  onDropCapture={handleDroppedImage}
                  onPaste={handlePastedImage}
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {previewImage ? (
                    <div className="pdp2-image-preview-state">
                      <img
                        className="pdp2-image-modal-preview"
                        src={previewImage}
                        alt="Selected profile preview"
                      />
                      <div className="pdp2-image-preview-meta">
                        <strong>{selectedFileName || "Selected image"}</strong>
                        <span>
                          {pendingProfilePictureValue
                            ? "Copied from another digital profile · ready to use"
                            : "Ready to use"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="pdp2-image-dropzone-empty">
                      <span className="pdp2-image-dropzone-icon">
                        <Upload size={24} aria-hidden="true" />
                      </span>
                      <strong>Drop or paste an image here</strong>
                      <span>
                        Drag from another digital profile, press Ctrl/Cmd+V, or click to choose
                      </span>
                      <small>PNG, JPG, JPEG or another browser-supported image</small>
                    </div>
                  )}

                  <input
                    ref={fileInputRef}
                    className="pdp2-image-file-input"
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      setUploadError("");
                      previewFile(event.target.files?.[0]);
                    }}
                  />
                </div>

                {uploadError && (
                  <div className="pdp2-image-upload-error" role="alert">
                    {uploadError}
                  </div>
                )}

                {previewImage && (
                  <button
                    type="button"
                    className="pdp2-image-change-button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <ImagePlus size={16} aria-hidden="true" />
                    Choose another image
                  </button>
                )}
              </div>

              <div className="pdp2-image-modal-footer">
                <button
                  type="button"
                  className="pdp2-image-modal-button pdp2-image-modal-button-secondary"
                  onClick={closeUpload}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="pdp2-image-modal-button pdp2-image-modal-button-primary"
                  onClick={handleProfileUpload}
                  disabled={!previewImage}
                >
                  Use picture
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <div className="pdp2-profile-image-editor">
        <div className="pdp2-profile-image-frame">
          {displayedImage ? (
            <img
              className="pdp2-profile-image"
              src={displayedImage}
              alt="Consultant"
              draggable
              onDragStart={handleProfileImageDragStart}
              title="Drag this image to another digital profile"
            />
          ) : readOnly ? (
            <div
              className="pdp2-profile-image-empty"
              aria-label="No profile picture"
            />
          ) : (
            <button
              type="button"
              className="pdp2-profile-image-empty"
              onClick={openUpload}
              data-pdp2-print-hide="true"
            >
              <ImagePlus size={28} aria-hidden="true" />
              <span>Add picture</span>
            </button>
          )}

          {!readOnly && displayedImage && (
            <div
              className="pdp2-profile-image-actions"
              data-pdp2-print-hide="true"
            >
              <button
                type="button"
                className="pdp2-profile-image-action"
                onClick={openUpload}
                title="Change profile picture"
                aria-label="Change profile picture"
              >
                <ImagePlus size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="pdp2-profile-image-action pdp2-profile-image-action-danger"
                onClick={handleProfileRemove}
                title="Remove profile picture"
                aria-label="Remove profile picture"
              >
                <Trash2 size={16} aria-hidden="true" />
              </button>
            </div>
          )}

          {!readOnly &&
            (exProfilePictureLoading ||
              exProfilePictureSuccess ||
              exProfilePictureError) && (
            <div
              className="pdp2-profile-image-status"
              data-pdp2-print-hide="true"
              title={
                exProfilePictureLoading
                  ? "Saving..."
                  : exProfilePictureSuccess
                    ? "Saved"
                    : "Error! Please reload"
              }
            >
              <img
                src={
                  exProfilePictureLoading
                    ? LoadingIcon
                    : exProfilePictureSuccess
                      ? successIcon
                      : errorIcon
                }
                alt=""
              />
            </div>
          )}
        </div>
      </div>

      {uploadModal}
    </>
  );
}

function Pdp2SectionToggle({
  checked,
  onChange,
  icon: Icon,
  label,
  description,
}) {
  return (
    <button
      type="button"
      className={`pdp2-section-option${checked ? " is-active" : ""}`}
      onClick={onChange}
      aria-pressed={checked}
    >
      <span className="pdp2-section-option-icon" aria-hidden="true">
        <Icon size={17} strokeWidth={2} />
      </span>

      <span className="pdp2-section-option-copy">
        <strong>{label}</strong>
        <small>{description}</small>
      </span>

      <span className="pdp2-section-option-switch" aria-hidden="true">
        <span />
      </span>
    </button>
  );
}

export default function PreviewDigitalPPT() {
  useEffect(() => {
    document.title = "Preview Digital PPT";
  }, []);

  const [expertName, setExpertName] = useState("");
  const [expertNameSuccess, setExpertNameSuccess] = useState(false);
  const [expertNameError, setExpertNameError] = useState(false);
  const [expertNameLoading, setExpertNameLoading] = useState(false);
  const [isShareName, setIsShareName] = useState("");
  const [displayID, setDisplayID] = useState(false);

  const handleIsShareNameChange = (value) => {
    setIsShareName(value);
  };
  const handleDisplayIDChnage = (value) => {
    setDisplayID(value);
  };

  const currentYear = new Date().getFullYear();

  const proj_id = useParams().proj_id;
  const exp_id = useParams().exp_id;
  const exprj_id = useParams().exprj_id;
  const location = useLocation();
  const isReadOnly = location.pathname.includes("/ExistingDigitalPPT");
  const { API_URL } = useContext(UrlContext);
  const [exDigitalID, setExDigitalID] = useState("");
  const [exDigitalIDSuccess, setExDigitalIDSuccess] = useState(false);
  const [exDigitalIDError, setExDigitalIDError] = useState(false);
  const [exDigitalIDLoading, setExDigitalIDLoading] = useState(false);
  const [powerPointID, setPowerPointID] = useState(null);

  const [exPosition, setExPosition] = useState("");
  const [exPositionSuccess, setExPositionSuccess] = useState(false);
  const [exPositionError, setExPositionError] = useState(false);
  const [exPositionLoading, setExPositionLoading] = useState(false);

  const [exLocation, setExLocation] = useState("");
  const [exLocationSuccess, setExLocationSuccess] = useState(false);
  const [exLocationError, setExLocationError] = useState(false);
  const [exLocationLoading, setExLocationLoading] = useState(false);

  const [region, setRegion] = useState(null);
  const [exRegionSuccess, setExRegionSuccess] = useState(false);
  const [exRegionError, setExRegionError] = useState(false);
  const [exRegionLoading, setExRegionLoading] = useState(false);

  const [language, setLanguage] = useState(null);
  const [exLanguageLoading, setExLanguageLoading] = useState(false);
  const [exLanguageSuccess, setExLanguageSuccess] = useState(false);
  const [exLanguageError, setExLanguageError] = useState(false);

  const [exTotalExperience, setExTotalExperience] = useState("");
  const [exTotalExperienceSuccess, setExTotalExperienceSuccess] =
    useState(false);
  const [exTotalExperienceError, setExTotalExperienceError] = useState(false);
  const [exTotalExperienceLoading, setExTotalExperienceLoading] =
    useState(false);

  const [exAvailableComment, setExAvailableComment] = useState("");
  const [exAvailableCommentSuccess, setExAvailableCommentSuccess] =
    useState(false);
  const [exAvailableCommentError, setExAvailableCommentError] = useState(false);
  const [exAvailableCommentLoading, setExAvailableCommentLoading] =
    useState(false);

  const [exProjectCurrency, setExProjectCurrency] = useState("");
  const [exDailyRate, setExDailyRate] = useState("");
  const [exBillingFrequency, setExBillingFrequency] = useState("");
  const [exFootNote, setExFootNote] = useState("");
  const [exDailyRateSuccess, setExDailyRateSuccess] = useState(false);
  const [exDailyRateError, setExDailyRateError] = useState(false);
  const [exDailyRateLoading, setExDailyRateLoading] = useState(false);

  const [exSummary, setExSummary] = useState("");
  const [exSummarySuccess, setExSummarySuccess] = useState(false);
  const [exSummaryError, setExSummaryError] = useState(false);
  const [exSummaryLoading, setExSummaryLoading] = useState(false);

  const [exExperience, setExExperience] = useState("");
  const [exExperienceSuccess, setExExperienceSuccess] = useState(false);
  const [exExperienceError, setExExperienceError] = useState(false);
  const [exExperienceLoading, setExExperienceLoading] = useState(false);

  const [exEducation, setExEducation] = useState("");
  const [exEducationSuccess, setExEducationSuccess] = useState(false);
  const [exEducationError, setExEducationError] = useState(false);
  const [exEducationLoading, setExEducationLoading] = useState(false);

  const [prjDimension1, setPrjDimension1] = useState("");
  const [prjDimension1Success, setPrjDimension1Success] = useState(false);
  const [prjDimension1Error, setPrjDimension1Error] = useState(false);
  const [prjDimension1Loading, setPrjDimension1Loading] = useState(false);

  const [prjDimension2, setPrjDimension2] = useState("");
  const [prjDimension2Success, setPrjDimension2Success] = useState(false);
  const [prjDimension2Error, setPrjDimension2Error] = useState(false);
  const [prjDimension2Loading, setPrjDimension2Loading] = useState(false);

  const [prjDimension3, setPrjDimension3] = useState("");
  const [prjDimension3Success, setPrjDimension3Success] = useState(false);
  const [prjDimension3Error, setPrjDimension3Error] = useState(false);
  const [prjDimension3Loading, setPrjDimension3Loading] = useState(false);

  const [prjDimension4, setPrjDimension4] = useState("");
  const [prjDimension4Success, setPrjDimension4Success] = useState(false);
  const [prjDimension4Error, setPrjDimension4Error] = useState(false);
  const [prjDimension4Loading, setPrjDimension4Loading] = useState(false);

  const [prjDimension5, setPrjDimension5] = useState("");
  const [prjDimension5Success, setPrjDimension5Success] = useState(false);
  const [prjDimension5Error, setPrjDimension5Error] = useState(false);
  const [prjDimension5Loading, setPrjDimension5Loading] = useState(false);
  const [ratingSuggestion, setRatingSuggestion] = useState(null);

  const [exProfilePicture, setExProfilePicture] = useState("");

  const [projectType, setProjectType] = useState(null);

  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);
  const [showAiSummaryModal, setShowAiSummaryModal] = useState(false);
  const [aiCustomPrompt, setAiCustomPrompt] = useState("");

  const getInfo = () => {
    const url = `${API_URL}ppt-get-share-info`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setIsShareName(data.isShareName);
        setDisplayID(data.isDisplayID);
      });
  };

  const saveRegion = async () => {
    if (isReadOnly) return;
    setExRegionError(false);
    setExRegionLoading(true);
    const requestBody = {
      projectID: proj_id,
      expertID: exp_id,
      region: region,
    };
    try {
      let modifiedUrl = "save-expert-project-region";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json();
      if (responseData.status === "success") {
        console.log("Region saved successfully!");
        setExRegionLoading(false);
        setExRegionSuccess(true);
        setTimeout(() => {
          setExRegionSuccess(false);
        }, 1500);
      } else {
        console.error("Failed to get language");
        setExRegionLoading(false);
        setExRegionError(true);
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
      setExRegionLoading(false);
      setExRegionError(true);
    }
  };

  // Api for modifing exiting notes
  const saveLanguage = async () => {
    if (isReadOnly) return;
    setExLanguageError(false);
    setExLanguageLoading(true);
    const requestBody = {
      projectID: proj_id,
      expertID: exp_id,
      language: language,
    };
    try {
      let modifiedUrl = "save-expert-project-language";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json();
      if (responseData.status === "success") {
        console.log("Saved!");
        setExLanguageLoading(false);
        setExLanguageSuccess(true);
        setTimeout(() => {
          setExLanguageSuccess(false);
        }, 1500);
      } else {
        console.error("Failed to save language!");
        setExLanguageLoading(false);
        setExLanguageError(true);
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
      setExLanguageLoading(false);
      setExLanguageError(true);
    }
  };

  // Api to fetch project dimension suggestions
  const getRatingSuggestions = async () => {
    const requestBody = {
      projectID: proj_id,
      expertID: exp_id,
    };
    try {
      let modifiedUrl = "get-rating-suggestions";
      const response = await fetch(`${API_URL}${modifiedUrl}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseData = await response.json();
      if (responseData.status === "success") {
        setRatingSuggestion(responseData.ratingSuggestion);
      } else {
        console.error("Failed to get language");
      }
    } catch (error) {
      console.error("Error while calling the backend API:", error);
    }
  };

  const showDivBasedOnUrl = () => {
    const { pathname } = location;
    if (pathname.includes("/PreviewDigitalPPT")) {
      // Render div for PreviewDigitalPPT
      return (
        <div className="ppt-previewBtn-div">
          {" "}
          <div
            class="custom-control custom-control-right custom-switch"
            style={{ float: "left", paddingTop: "8px" }}
          >
            <SharedInfoSelection
              projectID={proj_id}
              expertID={exp_id}
              onDisplayIDChange={handleDisplayIDChnage}
              onIsShareNameChange={handleIsShareNameChange}
            />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAiSummaryModal(true)}
            disabled={aiSummaryLoading}
            style={{ marginRight: "10px" }}
          >
            {aiSummaryLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="ms-1">Generating...</span>
              </>
            ) : (
              "AI Summary"
            )}
          </button>
          <button
            type="button"
            className="btn btn-light"
            onClick={() => window.close()}
          >
            Close
          </button>
          <ExistingDigital exp_id={exp_id} proj_id={proj_id} />
          {/* <AutomatedSummary exp_id={exp_id} proj_id={proj_id} exSummary={exSummary} /> */}
          {/* <button type="button" className="btn base-btn" onClick={handleSaveButton} >Save</button> */}
          {/* <button
            type="button"
            className="btn base-btn"
            onClick={handleDownloadButton}
          >
            Download
          </button> */}
          {/* <div className="updateinfo-ppt-div">
            <button
              type="button"
              className="btn base-btn"
              onClick={handleUpdateButton}
            >
              Get Info
            </button>
            <span className="updateinfo-ppt-tooltiptext">
              This action will delete all current changes and replace them with
              expert profile's information from Database!
            </span>
          </div> */}
        </div>
      );
    } else if (pathname.includes("/ExistingDigitalPPT")) {
      // Render div for ExistingDigitalPPT
      return (
        <div className="ppt-previewBtn-div">
          <b style={{ color: "#cb2121" }}>
            This is a Read-only existing digital PPT. Changes will not be saved.
            You can only copy information and images manually.
          </b>
        </div>
      );
    }
  };

  const [pptDownloading, setPptDownloading] = useState(false);
  const [digitalUpdating, setDigitalUpdating] = useState(false);
  // const [pptSaving, setPptSaving] = useState(false)
  // const [project, setProject] = useState({})
  const [showRateDimensionPopup, setShowRateDimensionPopup] = useState(false);
  const [ratingObj, setRatingObj] = useState({
    rating1: 0,
    rating2: 0,
    rating3: 0,
    rating4: 0,
    rating5: 0,
  });
  const [ratingObjSuccess, setRatingObjSuccess] = useState(false);
  const [ratingObjError, setRatingObjError] = useState(false);
  const [ratingObjLoading, setRatingObjLoading] = useState(false);

  const handleDownloadButton = () => {
    const authToken = localStorage.getItem("accesstoken");
    setPptDownloading(true);
    const url = `${API_URL}download-digital-ppt`;
    const data = {
      proj_id,
      exp_id,
      authToken,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setPptDownloading(false);
        if (data.status == "success") {
          window.open(data.fileUrl);
        }
        if (data.status === "error") {
          Swal.fire({
            icon: "error",
            title: "Oops...",
            text: "Something went wrong!",
            timer: 1500,
            showConfirmButton: false,
          });
        }
      })
      .catch((err) => {
        setPptDownloading(false);
        Swal.fire({
          icon: "error",
          title: "Oops...",
          text: "Something went wrong!",
          timer: 1500,
          showConfirmButton: false,
        });
      });
  };

  const generateAiSummary = async (customPrompt = aiCustomPrompt) => {
    if (isReadOnly) return;
    setAiSummaryLoading(true);
    try {
      const response = await fetch(
        `https://ai.expertpowerhouse.com/expert-search/generate-expert-match-summary`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            projectID: proj_id,
            expertID: exp_id,
            customPrompt: typeof customPrompt === "string" ? customPrompt.trim() : "",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }
      const data = await response.json();

      if (data.status === "success" && data.match_summary) {
        const rawSummary = data.match_summary.trim();
        // Strictly eliminate leading hyphens/dashes and format each bullet with '• '
        const extractedSummary = rawSummary
          .split("\n")
          .map((line) => {
            const trimmed = line.trim();
            if (/^[\-*•–—]/.test(trimmed)) {
              const withoutPrefix = trimmed.replace(/^[\s\-*•–—]+/, "").trim();
              return withoutPrefix ? `• ${withoutPrefix}` : "";
            }
            return line;
          })
          .join("\n");

        setExSummary(extractedSummary);
        setShowAiSummaryModal(false);

        // Focus on the ReactQuill component after setting the summary
        setTimeout(() => {
          // Find the ReactQuill editor div and focus on it
          const editorElement = document.querySelector(
            '.ql-editor[data-gramm="false"]'
          );
          if (editorElement) {
            editorElement.focus();
            // Trigger a click to ensure the component is properly focused
            editorElement.click();
          }
        }, 500); // Small delay to ensure React has updated the DOM

        Swal.fire({
          icon: "success",
          title: "AI Summary Generated",
          text: "The AI-generated summary has been inserted.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Processing Error",
          text: "Could not retrieve summary from AI response. Please try again.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error generating AI summary:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to generate AI summary. Please try again.",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setAiSummaryLoading(false);
    }
  };

  useEffect(() => {
    const url = `${API_URL}project-title/` + proj_id;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        // setProject(data.project)
        setPrjDimension1(data.project.dimension1);
        setPrjDimension2(data.project.dimension2);
        setPrjDimension3(data.project.dimension3);
        setPrjDimension4(data.project.dimension4);
        setPrjDimension5(data.project.dimension5);
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    const url = `${API_URL}view-digital-profile/${exp_id}/${proj_id}`;
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setPowerPointID(data.powerpointData.ID);
          setExDigitalID(data.powerpointData.expertDigitalID);
          setExpertName(data.powerpointData.expertName);
          setExPosition(data.powerpointData.position);
          setExLocation(data.powerpointData.location);
          setExTotalExperience(data.powerpointData.totalExperience);
          setExAvailableComment(data.powerpointData.Available_comment);
          setExProjectCurrency(data.powerpointData.projectCurrency);
          setExDailyRate(data.powerpointData.daily_rate);
          setExBillingFrequency(data.powerpointData.billingFrequency);
          setExFootNote(data.powerpointData.footNote);
          setExSummary(data.powerpointData.summ);
          setExExperience(data.powerpointData.experience);
          setExEducation(data.powerpointData.education);
          setExProfilePicture(data.powerpointData.profilePicture);
          setRegion(data.powerpointData.region);
          setLanguage(data.powerpointData.language);

          const storedIdentityVisible = pdp2StoredBoolean(
            data.powerpointData.hasNameSection,
            true
          );

          const storedIdentityMode = !storedIdentityVisible
            ? null
            : data.powerpointData.isNameShared === true
              ? "name"
              : data.powerpointData.isIdShared === true
                ? "id"
                : "id";

          setSectionVisibility({
            profilePicture: pdp2StoredBoolean(
              data.powerpointData.hasPictureSection,
              true
            ),
            identity: storedIdentityVisible,
            position: pdp2StoredBoolean(
              data.powerpointData.hasPositionSection,
              true
            ),
            location: pdp2StoredBoolean(
              data.powerpointData.hasLocationSection,
              true
            ),
            region: pdp2StoredBoolean(
              data.powerpointData.hasRegionSection,
              true
            ),
            language: pdp2StoredBoolean(
              data.powerpointData.hasLanguageSection,
              true
            ),
            totalExperience: pdp2StoredBoolean(
              data.powerpointData.hasTotalExperienceSection,
              true
            ),
            availability: pdp2StoredBoolean(
              data.powerpointData.hasAvailabilitySection,
              true
            ),
            rate: pdp2StoredBoolean(
              data.powerpointData.hasRateSection,
              true
            ),
            summary: pdp2StoredBoolean(
              data.powerpointData.hasSummarySection,
              true
            ),
            matching: pdp2StoredBoolean(
              data.powerpointData.hasMatchingSection,
              true
            ),
            experience: pdp2StoredBoolean(
              data.powerpointData.hasExperienceSection,
              true
            ),
            education: pdp2StoredBoolean(
              data.powerpointData.hasEducationSection,
              true
            ),
          });

          setIdentityMode(storedIdentityMode);

          setRatingObj({
            rating1: data.powerpointData.rating1,
            rating2: data.powerpointData.rating2,
            rating3: data.powerpointData.rating3,
            rating4: data.powerpointData.rating4,
            rating5: data.powerpointData.rating5,
          });
          setProjectType(data.powerpointData.projectType);
          getInfo();
          getRatingSuggestions();
        } else {
          console.log("Error while fetching data!");
          Swal.fire({
            title: "Error!",
            text: data.text,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const autoGrow = (event) => {
    const element = event.target;
    element.style.height = "5px";
    element.style.height = `${element.scrollHeight}px`;
  };

  useEffect(() => {
    setTimeout(() => {
      $("#ppt-expertExperience").trigger("click");
      $("#ppt-expert-education").trigger("click");
    }, 1800);
  }, []);

  const removeFormatting = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  const saveExDigitalID = () => {
    if (isReadOnly) return;
    setExDigitalIDError(false);
    setExDigitalIDLoading(true);
    const url = `${API_URL}ppt-save-expert-digital-id`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      expertDigitalID: exDigitalID,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExDigitalIDLoading(false);
        if (data.status === "success") {
          setExDigitalIDSuccess(true);
          setTimeout(() => {
            setExDigitalIDSuccess(false);
          }, 1500);
        } else {
          setExDigitalIDError(true);
        }
      })
      .catch((err) => {
        setExDigitalIDLoading(false);
        setExDigitalIDError(true);
      });
  };

  const saveExpertName = () => {
    if (isReadOnly) return;
    setExpertNameError(false);
    setExpertNameLoading(true);
    const url = `${API_URL}ppt-save-expert-name`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      expertName: expertName,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExpertNameLoading(false);
        if (data.status === "success") {
          setExpertNameSuccess(true);
          setTimeout(() => {
            setExpertNameSuccess(false);
          }, 1500);
        } else {
          setExpertNameError(true);
        }
      })
      .catch((err) => {
        setExpertNameLoading(false);
        setExpertNameError(true);
      });
  };

  const saveExPosition = () => {
    if (isReadOnly) return;
    setExPositionError(false);
    setExPositionLoading(true);
    const url = `${API_URL}ppt-save-expert-position`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      position: exPosition,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExPositionLoading(false);
        if (data.status === "success") {
          setExPositionSuccess(true);
          setTimeout(() => {
            setExPositionSuccess(false);
          }, 1500);
        } else {
          setExPositionError(true);
        }
      })
      .catch((err) => {
        setExPositionLoading(false);
        setExPositionError(true);
      });
  };

  const saveExLocation = () => {
    if (isReadOnly) return;
    setExLocationError(false);
    setExLocationLoading(true);
    const url = `${API_URL}ppt-save-expert-location`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      location: exLocation,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExLocationLoading(false);
        if (data.status === "success") {
          setExLocationSuccess(true);
          setTimeout(() => {
            setExLocationSuccess(false);
          }, 1500);
        } else {
          setExLocationError(true);
        }
      })
      .catch((err) => {
        setExLocationLoading(false);
        setExLocationError(true);
      });
  };

  const saveExTotalExperience = () => {
    if (isReadOnly) return;
    setExTotalExperienceError(false);
    setExTotalExperienceLoading(true);
    const url = `${API_URL}ppt-save-expert-total-experience`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      totalExperience: exTotalExperience,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExTotalExperienceLoading(false);
        if (data.status === "success") {
          setExTotalExperienceSuccess(true);
          setTimeout(() => {
            setExTotalExperienceSuccess(false);
          }, 1500);
        } else {
          setExTotalExperienceError(true);
        }
      })
      .catch((err) => {
        setExTotalExperienceLoading(false);
        setExTotalExperienceError(true);
      });
  };

  const saveExAvailableComment = () => {
    if (isReadOnly) return;
    setExAvailableCommentError(false);
    setExAvailableCommentLoading(true);
    const url = `${API_URL}ppt-save-expert-available-comment`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      Available_comment: exAvailableComment,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExAvailableCommentLoading(false);
        if (data.status === "success") {
          setExAvailableCommentSuccess(true);
          setTimeout(() => {
            setExAvailableCommentSuccess(false);
          }, 1500);
        } else {
          setExAvailableCommentError(true);
        }
      })
      .catch((err) => {
        setExAvailableCommentLoading(false);
        setExAvailableCommentError(true);
      });
  };

  const saveExProjectCurrency = (currency) => {
    if (isReadOnly) return;
    setExDailyRateError(false);
    setExDailyRateLoading(true);
    const url = `${API_URL}ppt-save-expert-project-currency`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      projectCurrency: currency,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExDailyRateLoading(false);
        if (data.status === "success") {
          setExDailyRateSuccess(true);
          setTimeout(() => {
            setExDailyRateSuccess(false);
          }, 1500);
        } else {
          setExDailyRateError(true);
        }
      })
      .catch((err) => {
        setExDailyRateLoading(false);
        setExDailyRateError(true);
      });
  };

  const saveExDailyRate = () => {
    if (isReadOnly) return;
    setExDailyRateError(false);
    setExDailyRateLoading(true);
    const url = `${API_URL}ppt-save-expert-daily-rate`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      daily_rate: exDailyRate,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExDailyRateLoading(false);
        if (data.status === "success") {
          setExDailyRateSuccess(true);
          setTimeout(() => {
            setExDailyRateSuccess(false);
          }, 1500);
        } else {
          setExDailyRateError(true);
        }
      })
      .catch((err) => {
        setExDailyRateLoading(false);
        setExDailyRateError(true);
      });
  };

  const saveExBillingFrequency = () => {
    if (isReadOnly) return;
    setExDailyRateError(false);
    setExDailyRateLoading(true);
    const url = `${API_URL}ppt-save-expert-billing-frequency`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      billingFrequency: exBillingFrequency,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExDailyRateLoading(false);
        if (data.status === "success") {
          setExDailyRateSuccess(true);
          setTimeout(() => {
            setExDailyRateSuccess(false);
          }, 1500);
        } else {
          setExDailyRateError(true);
        }
      })
      .catch((err) => {
        setExDailyRateLoading(false);
        setExDailyRateError(true);
      });
  };

  const saveExFootNote = (footNote) => {
    if (isReadOnly) return;
    setExDailyRateError(false);
    setExDailyRateLoading(true);
    const url = `${API_URL}ppt-save-expert-foot-note`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      footNote: footNote,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExDailyRateLoading(false);
        if (data.status === "success") {
          setExDailyRateSuccess(true);
          setTimeout(() => {
            setExDailyRateSuccess(false);
          }, 1500);
        } else {
          setExDailyRateError(true);
        }
      })
      .catch((err) => {
        setExDailyRateLoading(false);
        setExDailyRateError(true);
      });
  };
  const saveExSummary = () => {
    if (isReadOnly) return;
    setExSummaryError(false);
    setExSummaryLoading(true);
    const url = `${API_URL}ppt-save-expert-summary`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      summ: exSummary,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExSummaryLoading(false);
        if (data.status === "success") {
          setExSummarySuccess(true);
          setTimeout(() => {
            setExSummarySuccess(false);
          }, 1500);
        } else {
          setExSummaryError(true);
        }
      })
      .catch((err) => {
        setExSummaryLoading(false);
        setExSummaryError(true);
      });
  };

  const saveExEducation = () => {
    if (isReadOnly) return;
    setExEducationError(false);
    setExEducationLoading(true);
    const url = `${API_URL}ppt-save-expert-education`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      education: exEducation,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setExEducationLoading(false);
        if (data.status === "success") {
          setExEducationSuccess(true);
          setTimeout(() => {
            setExEducationSuccess(false);
          }, 1500);
        } else {
          setExEducationError(true);
        }
      })
      .catch((err) => {
        setExEducationLoading(false);
        setExEducationError(true);
      });
  };

  const savePrjDimension1 = () => {
    if (isReadOnly) return;
    setPrjDimension1Error(false);
    setPrjDimension1Loading(true);
    const url = `${API_URL}save-dimension1/` + proj_id;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dimension1: prjDimension1 }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPrjDimension1Loading(false);
        if (data.status === "success") {
          setPrjDimension1Success(true);
          setTimeout(() => {
            setPrjDimension1Success(false);
          }, 1500);
        } else {
          setPrjDimension1Error(true);
        }
      })
      .catch((err) => {
        setPrjDimension1Loading(false);
        setPrjDimension1Error(true);
      });
  };

  const savePrjDimension2 = () => {
    if (isReadOnly) return;
    setPrjDimension2Error(false);
    setPrjDimension2Loading(true);
    const url = `${API_URL}save-dimension2/` + proj_id;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dimension2: prjDimension2 }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPrjDimension2Loading(false);
        if (data.status === "success") {
          setPrjDimension2Success(true);
          setTimeout(() => {
            setPrjDimension2Success(false);
          }, 1500);
        } else {
          setPrjDimension2Error(true);
        }
      })
      .catch((err) => {
        setPrjDimension2Loading(false);
        setPrjDimension2Error(true);
      });
  };

  const savePrjDimension3 = () => {
    if (isReadOnly) return;
    setPrjDimension3Error(false);
    setPrjDimension3Loading(true);
    const url = `${API_URL}save-dimension3/` + proj_id;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dimension3: prjDimension3 }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPrjDimension3Loading(false);
        if (data.status === "success") {
          setPrjDimension3Success(true);
          setTimeout(() => {
            setPrjDimension3Success(false);
          }, 1500);
        } else {
          setPrjDimension3Error(true);
        }
      })
      .catch((err) => {
        setPrjDimension3Loading(false);
        setPrjDimension3Error(true);
      });
  };

  const savePrjDimension4 = () => {
    if (isReadOnly) return;
    setPrjDimension4Error(false);
    setPrjDimension4Loading(true);
    const url = `${API_URL}save-dimension4/` + proj_id;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dimension4: prjDimension4 }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPrjDimension4Loading(false);
        if (data.status === "success") {
          setPrjDimension4Success(true);
          setTimeout(() => {
            setPrjDimension4Success(false);
          }, 1500);
        } else {
          setPrjDimension4Error(true);
        }
      })
      .catch((err) => {
        setPrjDimension4Loading(false);
        setPrjDimension4Error(true);
      });
  };

  const savePrjDimension5 = () => {
    if (isReadOnly) return;
    setPrjDimension5Error(false);
    setPrjDimension5Loading(true);
    const url = `${API_URL}save-dimension5/` + proj_id;
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ dimension5: prjDimension5 }),
    })
      .then((res) => res.json())
      .then((data) => {
        setPrjDimension5Loading(false);
        if (data.status === "success") {
          setPrjDimension5Success(true);
          setTimeout(() => {
            setPrjDimension5Success(false);
          }, 1500);
        } else {
          setPrjDimension5Error(true);
        }
      })
      .catch((err) => {
        setPrjDimension5Loading(false);
        setPrjDimension5Error(true);
      });
  };

  const saveExRatingObj = () => {
    if (isReadOnly) return;
    setRatingObjError(false);
    setRatingObjLoading(true);
    const url = `${API_URL}ppt-save-expert-rating-obj`;
    const data = {
      proj_id: proj_id,
      exp_id: exp_id,
      ratingObj: ratingObj,
    };
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => res.json())
      .then((data) => {
        setRatingObjLoading(false);
        if (data.status === "success") {
          setRatingObjSuccess(true);
          setTimeout(() => {
            setRatingObjSuccess(false);
            setShowRateDimensionPopup(false);
          }, 700);
        } else {
          setRatingObjError(true);
        }
      })
      .catch((err) => {
        setRatingObjLoading(false);
        setRatingObjError(true);
      });
  };


  // ---------------------------------------------------------------------------
  // Browser-rendered PowerPoint-sized profile layout (v2)
  // All new styles/classes for this implementation use the pdp2- namespace.
  // The legacy PreviewDigitalPPT CSS remains loaded for untouched legacy popups
  // and helper components, but the new slide layout does not depend on it.
  // ---------------------------------------------------------------------------
  const browserSlideRef = useRef(null);
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [identityMode, setIdentityMode] = useState("id");
  const [sectionVisibility, setSectionVisibility] = useState({
    profilePicture: true,
    identity: true,
    position: true,
    location: true,
    region: true,
    language: true,
    totalExperience: true,
    availability: true,
    rate: true,
    summary: true,
    matching: true,
    experience: true,
    education: true,
  });

  const savePdp2LayoutUpdates = async (updates) => {
    if (isReadOnly) return false;

    try {
      const response = await fetch(
        `${API_URL}update-digital-profile-layout`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            projectID: proj_id,
            expertID: exp_id,
            updates,
          }),
        }
      );

      const responseData = await response.json();

      if (!response.ok || responseData.status !== "success") {
        throw new Error(
          responseData.text || "Failed to update digital profile layout."
        );
      }

      return true;
    } catch (error) {
      console.error("Error saving digital profile layout:", error);

      Swal.fire({
        icon: "error",
        title: "Layout not saved",
        text: "The profile layout change could not be saved. Please try again.",
        timer: 1800,
        showConfirmButton: false,
      });

      return false;
    }
  };

  const togglePdp2Section = async (key) => {
    if (isReadOnly) return;

    if (key === "identity") {
      const previousVisible = sectionVisibility.identity;
      const previousMode = identityMode;
      const nextVisible = !previousVisible;
      const nextMode = nextVisible ? previousMode || "id" : null;

      setSectionVisibility((previous) => ({
        ...previous,
        identity: nextVisible,
      }));
      setIdentityMode(nextMode);

      const saved = await savePdp2LayoutUpdates({
        hasNameSection: nextVisible,
        isNameShared: nextVisible && nextMode === "name",
        isIdShared: nextVisible && nextMode === "id",
      });

      if (!saved) {
        setSectionVisibility((previous) => ({
          ...previous,
          identity: previousVisible,
        }));
        setIdentityMode(previousMode);
      }

      return;
    }

    const dbField = PDP2_SECTION_DB_FIELDS[key];
    if (!dbField) return;

    const previousVisible = sectionVisibility[key];
    const nextVisible = !previousVisible;

    setSectionVisibility((previous) => ({
      ...previous,
      [key]: nextVisible,
    }));

    const saved = await savePdp2LayoutUpdates({
      [dbField]: nextVisible,
    });

    if (!saved) {
      setSectionVisibility((previous) => ({
        ...previous,
        [key]: previousVisible,
      }));
    }
  };

  const handlePdp2IdentitySelection = async (mode) => {
    if (isReadOnly) return;

    const previousVisible = sectionVisibility.identity;
    const previousMode = identityMode;
    const isAlreadyActive =
      previousVisible && previousMode === mode;

    if (isAlreadyActive) {
      setIdentityMode(null);
      setSectionVisibility((previous) => ({
        ...previous,
        identity: false,
      }));

      const saved = await savePdp2LayoutUpdates({
        hasNameSection: false,
        isNameShared: false,
        isIdShared: false,
      });

      if (!saved) {
        setIdentityMode(previousMode);
        setSectionVisibility((previous) => ({
          ...previous,
          identity: previousVisible,
        }));
      }

      return;
    }

    setIdentityMode(mode);
    setSectionVisibility((previous) => ({
      ...previous,
      identity: true,
    }));

    const saved = await savePdp2LayoutUpdates({
      hasNameSection: true,
      isNameShared: mode === "name",
      isIdShared: mode === "id",
    });

    if (!saved) {
      setIdentityMode(previousMode);
      setSectionVisibility((previous) => ({
        ...previous,
        identity: previousVisible,
      }));
    }
  };

  const pdp2InformationOptions = [
    {
      key: "profilePicture",
      label: "Profile picture",
      description: "Consultant profile photo",
      icon: ImagePlus,
      checked: sectionVisibility.profilePicture,
      onChange: () => togglePdp2Section("profilePicture"),
    },
    {
      key: "ephId",
      label: "EPH ID",
      description: "Editable EPH_CON_ identifier",
      icon: Hash,
      checked: sectionVisibility.identity && identityMode === "id",
      onChange: () => handlePdp2IdentitySelection("id"),
    },
    {
      key: "name",
      label: "Name",
      description: "Consultant name · read-only",
      icon: UserRound,
      checked: sectionVisibility.identity && identityMode === "name",
      onChange: () => handlePdp2IdentitySelection("name"),
    },
    {
      key: "position",
      label: "Position",
      description: "Current role or headline",
      icon: BriefcaseBusiness,
      checked: sectionVisibility.position,
      onChange: () => togglePdp2Section("position"),
    },
    {
      key: "location",
      label: "Location",
      description: "Current consultant location",
      icon: MapPin,
      checked: sectionVisibility.location,
      onChange: () => togglePdp2Section("location"),
    },
    {
      key: "region",
      label: "Region",
      description: "Regional experience",
      icon: Globe2,
      checked: sectionVisibility.region,
      onChange: () => togglePdp2Section("region"),
    },
    {
      key: "language",
      label: "Languages",
      description: "Language capabilities",
      icon: Languages,
      checked: sectionVisibility.language,
      onChange: () => togglePdp2Section("language"),
    },
    {
      key: "totalExperience",
      label: "Total experience",
      description: "Years of professional experience",
      icon: BriefcaseBusiness,
      checked: sectionVisibility.totalExperience,
      onChange: () => togglePdp2Section("totalExperience"),
    },
    {
      key: "availability",
      label: "Availability",
      description: "Current availability note",
      icon: CalendarClock,
      checked: sectionVisibility.availability,
      onChange: () => togglePdp2Section("availability"),
    },
    {
      key: "rate",
      label: "Rate",
      description: "Currency, rate and billing unit",
      icon: Coins,
      checked: sectionVisibility.rate,
      onChange: () => togglePdp2Section("rate"),
    },
  ];

  const pdp2ContentOptions = [
    {
      key: "summary",
      label: "Professional Summary",
      description: "Main consultant summary",
      icon: FileText,
      checked: sectionVisibility.summary,
      onChange: () => togglePdp2Section("summary"),
    },
    {
      key: "matching",
      label: "Requirement Matching",
      description: "Dimensions and radar matching",
      icon: Target,
      checked: sectionVisibility.matching,
      onChange: () => togglePdp2Section("matching"),
    },
    {
      key: "experience",
      label: "Relevant Experience",
      description: "Selected professional experience",
      icon: BriefcaseBusiness,
      checked: sectionVisibility.experience,
      onChange: () => togglePdp2Section("experience"),
    },
    {
      key: "education",
      label: "Education & Certification",
      description: "Education and credentials",
      icon: GraduationCap,
      checked: sectionVisibility.education,
      onChange: () => togglePdp2Section("education"),
    },
  ];

  const pdp2InformationActiveCount = pdp2InformationOptions.filter(
    (option) => option.checked
  ).length;

  const pdp2ContentActiveCount = pdp2ContentOptions.filter(
    (option) => option.checked
  ).length;

  const pdp2SideFieldWeight = (value, base = 1) => {
    const textLength = String(value || "").trim().length;
    if (textLength > 95) return base + 3;
    if (textLength > 60) return base + 2;
    if (textLength > 30) return base + 1;
    return base;
  };

  const pdp2RadarValues = [
    Number(ratingObj?.rating1) || 0,
    Number(ratingObj?.rating2) || 0,
    Number(ratingObj?.rating3) || 0,
    Number(ratingObj?.rating4) || 0,
    Number(ratingObj?.rating5) || 0,
  ];

  const pdp2MatchingPercentage = Math.round(
    (pdp2RadarValues.reduce((sum, value) => sum + value, 0) / 25) * 100
  );

  const pdp2Dimensions = [
    {
      value: prjDimension1,
      setValue: setPrjDimension1,
      save: savePrjDimension1,
      loading: prjDimension1Loading,
      success: prjDimension1Success,
      error: prjDimension1Error,
    },
    {
      value: prjDimension2,
      setValue: setPrjDimension2,
      save: savePrjDimension2,
      loading: prjDimension2Loading,
      success: prjDimension2Success,
      error: prjDimension2Error,
    },
    {
      value: prjDimension3,
      setValue: setPrjDimension3,
      save: savePrjDimension3,
      loading: prjDimension3Loading,
      success: prjDimension3Success,
      error: prjDimension3Error,
    },
    {
      value: prjDimension4,
      setValue: setPrjDimension4,
      save: savePrjDimension4,
      loading: prjDimension4Loading,
      success: prjDimension4Success,
      error: prjDimension4Error,
    },
    {
      value: prjDimension5,
      setValue: setPrjDimension5,
      save: savePrjDimension5,
      loading: prjDimension5Loading,
      success: prjDimension5Success,
      error: prjDimension5Error,
    },
  ];

  const renderPdp2SaveState = (loading, success, error) => {
    if (!loading && !success && !error) return null;

    const src = loading ? LoadingIcon : success ? successIcon : errorIcon;
    const title = loading ? "Saving..." : success ? "Saved" : "Error! Please reload";

    return (
      <img
        className="pdp2-save-state"
        src={src}
        alt={title}
        title={title}
        data-pdp2-print-hide="true"
      />
    );
  };

  const renderPdp2HideButton = (key, label) =>
    isReadOnly ? null : (
    <button
      type="button"
      className="pdp2-hide-control"
      onClick={() => togglePdp2Section(key)}
      aria-label={`Hide ${label}`}
      title={`Hide ${label}`}
      data-pdp2-print-hide="true"
    >
      ×
    </button>
  );

  const copyPdp2DocumentStyles = (printWindow) => {
    Array.from(document.querySelectorAll('style, link[rel="stylesheet"]')).forEach(
      (node) => {
        try {
          printWindow.document.head.appendChild(node.cloneNode(true));
        } catch (error) {
          console.error("Failed to copy stylesheet to print window:", error);
        }
      }
    );
  };

  const waitForPdp2Images = async (doc) => {
    const images = Array.from(doc.images || []);
    await Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      })
    );
  };

  const handleBrowserPdf = async () => {
    const printableNode = browserSlideRef.current;
    if (!printableNode) return;

    const printWindow = window.open("", "_blank", "width=1400,height=900");
    if (!printWindow) return;

    const printableTitle = (projectType && projectType === "exp") ? `EPH_EXP_${exDigitalID || exp_id || "Profile"}` : `EPH_CON_${exDigitalID || exp_id || "Profile"}`;

    printWindow.document.open();
    printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>${printableTitle}</title>
        </head>
        <body>
          <div id="pdp2-print-root"></div>
        </body>
      </html>
    `);
    printWindow.document.close();

    copyPdp2DocumentStyles(printWindow);

    const printStyle = printWindow.document.createElement("style");
    printStyle.textContent = `
      @page {
        size: 13.333333333in 7.5in;
        margin: 0;
      }

      html,
      body,
      #pdp2-print-root {
        width: 13.333333333in !important;
        height: 7.5in !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: hidden !important;
        background: #ffffff !important;
      }

      .pdp2-slide {
        width: 13.333333333in !important;
        height: 7.5in !important;
        margin: 0 !important;
        box-shadow: none !important;
        overflow: hidden !important;
        break-after: page;
        page-break-after: always;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }

      .pdp2-screen-only,
      [data-pdp2-print-hide="true"] {
        display: none !important;
      }

      .pdp2-print-only {
        display: block !important;
      }
    `;
    printWindow.document.head.appendChild(printStyle);

    const clonedNode = printableNode.cloneNode(true);
    clonedNode
      .querySelectorAll('[data-pdp2-print-hide="true"], .pdp2-screen-only')
      .forEach((node) => node.remove());

    clonedNode.querySelectorAll(".pdp2-print-only").forEach((node) => {
      node.style.display = "block";
    });

    const mountPoint = printWindow.document.getElementById("pdp2-print-root");
    mountPoint.appendChild(clonedNode);

    await waitForPdp2Images(printWindow.document);

    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  console.log('projectType: ', projectType);

  return (
    <>
      <div className="pdp2-page-shell">
        <div className="pdp2-stage">
          <DigitalProfileSlide
            slideRef={browserSlideRef}
            readOnly={isReadOnly}
            sectionVisibility={sectionVisibility}
            projectID={proj_id}
            expertID={exp_id}
            powerPointID={powerPointID}
            summaryModules={modules}
            summaryFormats={formats}
            onRateDimensions={() => setShowRateDimensionPopup(true)}
            renderHideButton={renderPdp2HideButton}
            renderSaveState={renderPdp2SaveState}
            profileImageNode={
              <Pdp2ExpertImage
                proj_id={proj_id}
                exp_id={exp_id}
                exProfilePicture={exProfilePicture}
                setExProfilePicture={setExProfilePicture}
                readOnly={isReadOnly}
              />
            }
            data={{
              identityMode,
              projectType,
              profilePicture: exProfilePicture,
              expertDigitalID: exDigitalID,
              expertName,
              position: exPosition,
              location: exLocation,
              region,
              language,
              totalExperience: exTotalExperience,
              availableComment: exAvailableComment,
              projectCurrency: exProjectCurrency,
              dailyRate: exDailyRate,
              billingFrequency: exBillingFrequency,
              footNote: exFootNote,
              summary: exSummary,
              education: exEducation,
              dimensions: pdp2Dimensions.map((item) => item.value || ""),
              ratings: pdp2RadarValues,
            }}
            editing={{
              setDigitalID: setExDigitalID,
              saveDigitalID: saveExDigitalID,
              digitalIDLoading: exDigitalIDLoading,
              digitalIDSuccess: exDigitalIDSuccess,
              digitalIDError: exDigitalIDError,

              setPosition: setExPosition,
              savePosition: saveExPosition,
              positionLoading: exPositionLoading,
              positionSuccess: exPositionSuccess,
              positionError: exPositionError,

              setLocation: setExLocation,
              saveLocation: saveExLocation,
              locationLoading: exLocationLoading,
              locationSuccess: exLocationSuccess,
              locationError: exLocationError,

              setRegion,
              saveRegion,
              regionLoading: exRegionLoading,
              regionSuccess: exRegionSuccess,
              regionError: exRegionError,

              setLanguage,
              saveLanguage,
              languageLoading: exLanguageLoading,
              languageSuccess: exLanguageSuccess,
              languageError: exLanguageError,

              setTotalExperience: setExTotalExperience,
              saveTotalExperience: saveExTotalExperience,
              totalExperienceLoading: exTotalExperienceLoading,
              totalExperienceSuccess: exTotalExperienceSuccess,
              totalExperienceError: exTotalExperienceError,

              setAvailableComment: setExAvailableComment,
              saveAvailableComment: saveExAvailableComment,
              availableCommentLoading: exAvailableCommentLoading,
              availableCommentSuccess: exAvailableCommentSuccess,
              availableCommentError: exAvailableCommentError,

              setProjectCurrency: setExProjectCurrency,
              saveProjectCurrency: saveExProjectCurrency,
              setDailyRate: setExDailyRate,
              saveDailyRate: saveExDailyRate,
              setBillingFrequency: setExBillingFrequency,
              saveBillingFrequency: saveExBillingFrequency,
              setFootNote: setExFootNote,
              saveFootNote: saveExFootNote,
              dailyRateLoading: exDailyRateLoading,
              dailyRateSuccess: exDailyRateSuccess,
              dailyRateError: exDailyRateError,

              setSummary: setExSummary,
              saveSummary: saveExSummary,
              removeFormatting,
              summaryLoading: exSummaryLoading,
              summarySuccess: exSummarySuccess,
              summaryError: exSummaryError,

              setDimension: (index, value) =>
                pdp2Dimensions[index]?.setValue?.(value),
              saveDimension: (index) =>
                pdp2Dimensions[index]?.save?.(),
              dimensionLoading: pdp2Dimensions.map((item) => item.loading),
              dimensionSuccess: pdp2Dimensions.map((item) => item.success),
              dimensionError: pdp2Dimensions.map((item) => item.error),

              setEducation: setExEducation,
              saveEducation: saveExEducation,
              educationLoading: exEducationLoading,
              educationSuccess: exEducationSuccess,
              educationError: exEducationError,

              autoGrow,
            }}
          />
        </div>

        <div className="pdp2-toolbar" data-pdp2-print-hide="true">
          {location.pathname.includes("/PreviewDigitalPPT") ? (
            <>
              <div className="pdp2-toolbar-share-control">
                <SharedInfoSelection
                  projectID={proj_id}
                  expertID={exp_id}
                  onDisplayIDChange={handleDisplayIDChnage}
                  onIsShareNameChange={handleIsShareNameChange}
                />
              </div>

              <div className="pdp2-section-picker-wrap">
                <button
                  type="button"
                  className="pdp2-toolbar-button"
                  onClick={() => setShowSectionPicker(true)}
                >
                  Sections
                </button>

                {typeof document !== "undefined" &&
                  createPortal(
                    <AnimatePresence>
                      {showSectionPicker && (
                        <motion.div
                          className="fixed inset-0 z-[10050] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm sm:p-6"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.16 }}
                          role="presentation"
                          data-pdp2-print-hide="true"
                          onMouseDown={(event) => {
                            if (event.target === event.currentTarget) {
                              setShowSectionPicker(false);
                            }
                          }}
                        >
                          <motion.div
                            className="pdp2-section-picker flex max-h-[90vh] w-[min(1080px,calc(100vw-32px))] flex-col overflow-hidden rounded-[26px] border border-slate-200/90 bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_34%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_28px_90px_rgba(15,23,42,0.34)]"
                            initial={{ opacity: 0, y: 22, scale: 0.975 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 18, scale: 0.98 }}
                            transition={{
                              type: "spring",
                              stiffness: 340,
                              damping: 30,
                              mass: 0.82,
                            }}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="pdp2-section-picker-title"
                            onMouseDown={(event) => event.stopPropagation()}
                          >
                            <div className="pdp2-section-picker-header">
                              <div className="pdp2-section-picker-title-wrap">
                                <span className="pdp2-section-picker-eyebrow">
                                  Digital profile layout
                                </span>
                                <strong id="pdp2-section-picker-title">
                                  Choose visible sections
                                </strong>
                                <p>
                                  Control exactly which information and content blocks
                                  appear on this profile.
                                </p>
                              </div>

                              <button
                                type="button"
                                className="pdp2-section-picker-close"
                                onClick={() => setShowSectionPicker(false)}
                                aria-label="Close section picker"
                              >
                                ×
                              </button>
                            </div>

                            <div className="pdp2-section-picker-body">
                              <div className="pdp2-section-picker-sections">
                                <section className="pdp2-section-picker-panel pdp2-section-picker-panel-information">
                                  <div className="pdp2-section-picker-panel-header">
                                    <div className="pdp2-section-picker-panel-title">
                                      <span className="pdp2-section-picker-panel-icon">
                                        <UserRound size={17} strokeWidth={2} />
                                      </span>
                                      <div>
                                        <strong>Information</strong>
                                        <small>Left-side consultant details</small>
                                      </div>
                                    </div>
                                    <span className="pdp2-section-picker-count">
                                      {pdp2InformationActiveCount}/
                                      {pdp2InformationOptions.length}
                                    </span>
                                  </div>

                                  <div className="pdp2-section-picker-grid pdp2-section-picker-grid-information">
                                    {pdp2InformationOptions.map((option) => (
                                      <Pdp2SectionToggle
                                        key={option.key}
                                        checked={option.checked}
                                        onChange={option.onChange}
                                        icon={option.icon}
                                        label={option.label}
                                        description={option.description}
                                      />
                                    ))}
                                  </div>

                                  <div className="pdp2-section-picker-note">
                                    <Hash size={13} strokeWidth={2} />
                                    <span>
                                      <strong>EPH ID</strong> and{" "}
                                      <strong>Name</strong> share the same profile
                                      area and are mutually exclusive.
                                    </span>
                                  </div>
                                </section>

                                <section className="pdp2-section-picker-panel pdp2-section-picker-panel-content">
                                  <div className="pdp2-section-picker-panel-header">
                                    <div className="pdp2-section-picker-panel-title">
                                      <span className="pdp2-section-picker-panel-icon">
                                        <FileText size={17} strokeWidth={2} />
                                      </span>
                                      <div>
                                        <strong>Content</strong>
                                        <small>Main profile sections</small>
                                      </div>
                                    </div>
                                    <span className="pdp2-section-picker-count">
                                      {pdp2ContentActiveCount}/
                                      {pdp2ContentOptions.length}
                                    </span>
                                  </div>

                                  <div className="pdp2-section-picker-grid pdp2-section-picker-grid-content">
                                    {pdp2ContentOptions.map((option) => (
                                      <Pdp2SectionToggle
                                        key={option.key}
                                        checked={option.checked}
                                        onChange={option.onChange}
                                        icon={option.icon}
                                        label={option.label}
                                        description={option.description}
                                      />
                                    ))}
                                  </div>
                                </section>
                              </div>
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>,
                    document.body
                  )}
              </div>

              <button
                type="button"
                className="pdp2-toolbar-button pdp2-toolbar-button-primary flex items-center gap-1.5"
                onClick={() => setShowAiSummaryModal(true)}
                disabled={aiSummaryLoading}
              >
                {aiSummaryLoading ? "Generating..." : "AI Summary"}
              </button>

              <button
                type="button"
                className="pdp2-toolbar-button pdp2-toolbar-button-primary"
                onClick={handleBrowserPdf}
              >
                Save PDF
              </button>

              <div className="pdp2-toolbar-existing">
                <ExistingDigital exp_id={exp_id} proj_id={proj_id} />
              </div>

              <button
                type="button"
                className="pdp2-toolbar-button"
                onClick={() => window.close()}
              >
                Close
              </button>
            </>
          ) : (
            <>
              <span className="pdp2-readonly-message">
                This is a read-only existing digital profile. Changes will not be saved.
              </span>
              <button
                type="button"
                className="pdp2-toolbar-button pdp2-toolbar-button-primary"
                onClick={handleBrowserPdf}
              >
                Save PDF
              </button>
            </>
          )}
        </div>
      </div>

      {/* ------------------------------- Rating popup -------------------------- */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {!isReadOnly && showRateDimensionPopup && (
              <motion.div
                className="fixed inset-0 z-[10060] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm sm:p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.16 }}
                role="presentation"
                data-pdp2-print-hide="true"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) {
                    setShowRateDimensionPopup(false);
                  }
                }}
              >
                <motion.div
                  className="pdp2-section-picker flex max-h-[90vh] w-[min(960px,calc(100vw-32px))] flex-col overflow-hidden rounded-[26px] border border-slate-200/90 bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_34%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_28px_90px_rgba(15,23,42,0.34)]"
                  initial={{ opacity: 0, y: 22, scale: 0.975 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 18, scale: 0.98 }}
                  transition={{
                    type: "spring",
                    stiffness: 340,
                    damping: 30,
                    mass: 0.82,
                  }}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="pdp2-rating-popup-title"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <div className="pdp2-section-picker-header">
                    <div className="pdp2-section-picker-title-wrap">
                      <span className="pdp2-section-picker-eyebrow">
                        Requirement matching
                      </span>

                      <strong id="pdp2-rating-popup-title">
                        Rate the dimensions
                      </strong>

                      <p>
                        Adjust each matching dimension from 0 to 5. Existing
                        project data is shown alongside as a reference.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="pdp2-section-picker-close"
                      onClick={() => setShowRateDimensionPopup(false)}
                      aria-label="Close rating popup"
                    >
                      ×
                    </button>
                  </div>

                  <div className="pdp2-section-picker-body overflow-y-auto">
                    <div className="grid grid-cols-1 gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                      <section className="rounded-2xl border border-slate-200/90 bg-white/75 p-4 shadow-[0_8px_26px_rgba(15,23,42,0.04)]">
                        <div className="mb-3 flex items-start gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#365561]/10 text-[#365561]">
                            <Target size={17} strokeWidth={2} />
                          </span>

                          <div>
                            <strong className="block text-[13px] font-semibold text-slate-800">
                              Current ratings
                            </strong>
                            <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-slate-500">
                              Enter a score between 0 and 5 for each
                              project dimension.
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2.5">
                          {[
                            {
                              key: "rating1",
                              dimension: prjDimension1,
                            },
                            {
                              key: "rating2",
                              dimension: prjDimension2,
                            },
                            {
                              key: "rating3",
                              dimension: prjDimension3,
                            },
                            {
                              key: "rating4",
                              dimension: prjDimension4,
                            },
                            {
                              key: "rating5",
                              dimension: prjDimension5,
                            },
                          ].map((item, index) => (
                            <label
                              key={item.key}
                              className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 transition focus-within:border-[#365561]/35 focus-within:ring-2 focus-within:ring-[#365561]/10"
                            >
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[10.5px] font-semibold text-slate-500">
                                {index + 1}
                              </span>

                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-[11.5px] font-semibold text-slate-700">
                                  {item.dimension || `Dimension ${index + 1}`}
                                </span>
                              </span>

                              <div className="flex shrink-0 items-center gap-1.5">
                                <input
                                  type="number"
                                  min={0}
                                  max={5}
                                  value={ratingObj[item.key]}
                                  onChange={(event) => {
                                    if (
                                      event.target.value >= 0 &&
                                      event.target.value <= 5
                                    ) {
                                      setRatingObj({
                                        ...ratingObj,
                                        [item.key]: event.target.value,
                                      });
                                    }
                                  }}
                                  className="h-8 w-[62px] rounded-lg border border-slate-200 bg-slate-50 px-2 text-center text-[12px] font-semibold text-[#365561] outline-none transition focus:border-[#365561]/40 focus:bg-white"
                                  aria-label={`Rating for ${
                                    item.dimension || `dimension ${index + 1}`
                                  }`}
                                />

                                <span className="text-[10px] font-medium text-slate-400">
                                  / 5
                                </span>
                              </div>
                            </label>
                          ))}
                        </div>
                      </section>

                      <section className="rounded-2xl border border-slate-200/90 bg-white/75 p-4 shadow-[0_8px_26px_rgba(15,23,42,0.04)]">
                        <div className="mb-3 flex items-start gap-3">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[#365561]/10 text-[#365561]">
                            <History size={17} strokeWidth={2} />
                          </span>

                          <div>
                            <strong className="block text-[13px] font-semibold text-slate-800">
                              Suggested ratings
                            </strong>
                            <span className="mt-0.5 block text-[11.5px] leading-[1.45] text-slate-500">
                              Based on previous project data available for
                              this consultant.
                            </span>
                          </div>
                        </div>

                        {ratingSuggestion ? (
                          <div className="space-y-2.5">
                            {[
                              ratingSuggestion.rating1,
                              ratingSuggestion.rating2,
                              ratingSuggestion.rating3,
                              ratingSuggestion.rating4,
                              ratingSuggestion.rating5,
                            ].map((suggestion, index) => (
                              <div
                                key={index}
                                className="flex min-h-[53px] items-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50/75 px-3 py-2.5"
                              >
                                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-[10.5px] font-semibold text-slate-500 shadow-sm">
                                  {index + 1}
                                </span>

                                <div className="min-w-0 flex-1">
                                  <strong className="block truncate text-[11.5px] font-semibold text-slate-700">
                                    {suggestion?.dimension ||
                                      `Dimension ${index + 1}`}
                                  </strong>

                                  {suggestion?.value ? (
                                    <span className="mt-0.5 block text-[10px] text-slate-400">
                                      Based on{" "}
                                      {suggestion.projectCount || 0}{" "}
                                      project
                                      {Number(suggestion.projectCount) === 1
                                        ? ""
                                        : "s"}
                                    </span>
                                  ) : (
                                    <span className="mt-0.5 block text-[10px] text-slate-400">
                                      No historical data found
                                    </span>
                                  )}
                                </div>

                                {suggestion?.value ? (
                                  <span className="shrink-0 rounded-lg bg-[#365561] px-2.5 py-1 text-[11px] font-semibold text-white">
                                    {suggestion.value}/5
                                  </span>
                                ) : (
                                  <span className="shrink-0 rounded-lg bg-slate-200 px-2 py-1 text-[9.5px] font-medium text-slate-500">
                                    No data
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="flex min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50/60 px-6 text-center">
                            <History
                              size={22}
                              strokeWidth={1.8}
                              className="text-slate-400"
                            />

                            <strong className="mt-2 text-[11.5px] font-semibold text-slate-600">
                              No rating suggestions available
                            </strong>

                            <span className="mt-1 max-w-[280px] text-[10px] leading-[1.5] text-slate-400">
                              No usable historical project data was found
                              for this consultant.
                            </span>
                          </div>
                        )}
                      </section>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 border-t border-slate-200/80 bg-white/70 px-5 py-3.5">
                    <div className="min-h-[24px]">
                      {ratingObjLoading && (
                        <div className="flex items-center gap-2 text-[10.5px] font-medium text-slate-500">
                          <img
                            src={LoadingIcon}
                            alt=""
                            className="h-4 w-4 object-contain"
                          />
                          Saving ratings...
                        </div>
                      )}

                      {ratingObjSuccess && (
                        <div className="flex items-center gap-2 text-[10.5px] font-medium text-emerald-600">
                          <img
                            src={successIcon}
                            alt=""
                            className="h-4 w-4 object-contain"
                          />
                          Ratings saved
                        </div>
                      )}

                      {ratingObjError && (
                        <div className="flex items-center gap-2 text-[10.5px] font-medium text-red-600">
                          <img
                            src={errorIcon}
                            alt=""
                            className="h-4 w-4 object-contain"
                          />
                          Could not save ratings
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[11.5px] font-semibold text-slate-600 transition hover:bg-slate-50 focus:outline-none"
                        onClick={() => setShowRateDimensionPopup(false)}
                        disabled={ratingObjLoading}
                      >
                        Close
                      </button>

                      <button
                        type="button"
                        className="rounded-xl bg-[#365561] px-4 py-2 text-[11.5px] font-semibold text-white transition hover:bg-[#2d4852] focus:outline-none disabled:cursor-wait disabled:opacity-60"
                        onClick={saveExRatingObj}
                        disabled={ratingObjLoading}
                      >
                        {ratingObjLoading ? "Saving..." : "Save ratings"}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

      {/* ------------------------------- AI Summary modal -------------------------- */}
      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {!isReadOnly && showAiSummaryModal && (
              <motion.div
                className="fixed inset-0 z-[10060] flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm sm:p-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.16 }}
                role="presentation"
                data-pdp2-print-hide="true"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget && !aiSummaryLoading) {
                    setShowAiSummaryModal(false);
                  }
                }}
              >
                <motion.div
                  className="pdp2-section-picker flex max-h-[90vh] w-[min(560px,calc(100vw-32px))] flex-col overflow-hidden rounded-[26px] border border-slate-200/90 bg-[radial-gradient(circle_at_top,rgba(54,85,97,0.14),transparent_34%),linear-gradient(to_bottom,#f8fafc,#eef2f6)] shadow-[0_28px_90px_rgba(15,23,42,0.34)]"
                  initial={{ opacity: 0, y: 22, scale: 0.975 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 18, scale: 0.98 }}
                  transition={{
                    type: "spring",
                    stiffness: 340,
                    damping: 30,
                    mass: 0.82,
                  }}
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="pdp2-ai-modal-title"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <div className="pdp2-section-picker-header">
                    <div className="pdp2-section-picker-title-wrap">
                      <span className="pdp2-section-picker-eyebrow flex items-center gap-1.5">
                        <Sparkles size={13} className="text-[#365561]" />
                        AI Summary Assistant
                      </span>

                      <strong id="pdp2-ai-modal-title">
                        Generate Professional Summary
                      </strong>

                      <p>
                        Add optional focus instructions to guide the summary while preserving verified candidate profile and project data.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="pdp2-section-picker-close"
                      onClick={() => !aiSummaryLoading && setShowAiSummaryModal(false)}
                      aria-label="Close AI Summary popup"
                      disabled={aiSummaryLoading}
                    >
                      ×
                    </button>
                  </div>

                  <div className="pdp2-section-picker-body overflow-y-auto p-5">
                    <div className="rounded-2xl border border-slate-200/90 bg-white/85 p-4 shadow-[0_8px_26px_rgba(15,23,42,0.04)]">
                      <label className="block text-[12.5px] font-semibold text-slate-800 mb-1.5">
                        Additional Focus / Custom Instructions (Optional)
                      </label>
                      <span className="block text-[11px] text-slate-500 mb-2 leading-relaxed">
                        Specify particular angles, industries, or experiences you want emphasized in the summary.
                      </span>

                      <textarea
                        className="w-full h-32 p-3 text-[13px] text-slate-800 bg-white border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#365561]/40 focus:border-[#365561] transition-all resize-none placeholder:text-slate-400"
                        placeholder="E.g., Emphasize leadership in digital banking transformation, highlight M&A integrations, focus on European supply chain optimizations..."
                        value={aiCustomPrompt}
                        onChange={(e) => setAiCustomPrompt(e.target.value)}
                        disabled={aiSummaryLoading}
                      />

                      <div className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 p-2.5 text-[11px] text-slate-600 border border-slate-200/60 leading-relaxed">
                        <span className="text-[#365561] font-bold">ℹ</span>
                        <span>
                          <strong>Standard Format Maintained:</strong> Generates a 1-2 sentence match justification followed by the <em>Previous Experience</em> bullet points. Facts are strictly extracted from the expert&apos;s verified profile and CV.
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-3 pt-2">
                      <button
                        type="button"
                        className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-[11.5px] font-semibold text-slate-700 transition hover:bg-slate-100 focus:outline-none disabled:opacity-50"
                        onClick={() => setShowAiSummaryModal(false)}
                        disabled={aiSummaryLoading}
                      >
                        Cancel
                      </button>

                      <button
                        type="button"
                        className="flex items-center gap-1.5 rounded-xl bg-[#365561] px-4 py-2 text-[11.5px] font-semibold text-white transition hover:bg-[#2d4852] focus:outline-none disabled:cursor-wait disabled:opacity-60"
                        onClick={() => generateAiSummary()}
                        disabled={aiSummaryLoading}
                      >
                        {aiSummaryLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm"
                              role="status"
                              aria-hidden="true"
                            />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} />
                            <span>Generate Summary</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}


      {pptDownloading && (
        <div className="linkedin-popup-wrapper">
          <div className="tab-pane fade show linkedin-div">
            <center>
              <p style={{ fontSize: "18px" }}>Downloading PPT . . .</p>
              <br />
              <img
                src={LoadingIcon}
                alt=""
                style={{ width: "50px", marginRight: "5px" }}
              />
            </center>
          </div>
        </div>
      )}

      {/* {pptSaving && (
        <div className="linkedin-popup-wrapper">
          <div className="tab-pane fade show linkedin-div">
            <center>
                <p style={{fontSize: '18px'}}>Saving PPT . . .</p><br />
                <img src={LoadingIcon} alt="" style={{ width: '50px', marginRight: '5px' }} />
            </center>
          </div>
        </div>
      )} */}

      {digitalUpdating && (
        <div className="linkedin-popup-wrapper">
          <div className="tab-pane fade show linkedin-div">
            <center>
              <p style={{ fontSize: "18px" }}>Updating information . . .</p>
              <br />
              <img
                src={LoadingIcon}
                alt=""
                style={{ width: "50px", marginRight: "5px" }}
              />
            </center>
          </div>
        </div>
      )}
    </>
  );
}
